/**
 * Cloisonnement des caches locaux par compte.
 *
 * Une cle globale ne convient pas aux donnees de jeu : deux comptes utilises
 * sur le meme navigateur finiraient par lire puis sauvegarder le meme blob.
 * Les preferences reellement liees a l'appareil (theme, audio...) restent,
 * elles, volontairement globales.
 */

export const ACCOUNT_CHANGED_EVENT = 'guild-account-changed';

// Un compte supprimé ne doit plus pouvoir être repersisté par un watcher,
// visibilitychange ou beforeunload encore en attente pendant la redirection.
const persistenceBlockedAccounts = new Set<string>();

export function blockAccountPersistence(accountId: string): void {
  const id = String(accountId || '').trim();
  if (id) persistenceBlockedAccounts.add(id);
}

export function unblockAccountPersistence(accountId: string): void {
  persistenceBlockedAccounts.delete(String(accountId || '').trim());
}

export function isAccountPersistenceBlocked(accountId: string): boolean {
  const id = String(accountId || '').trim();
  return !!id && persistenceBlockedAccounts.has(id);
}

export function storedAccountId(): string {
  try {
    return localStorage.getItem('guild_deviceId') || '';
  } catch {
    return '';
  }
}

export function accountStorageKey(baseKey: string, accountId: string): string {
  const safeAccountId = encodeURIComponent(String(accountId || '').trim());
  return safeAccountId ? `${baseKey}:account:${safeAccountId}` : '';
}

export function readAccountItem(baseKey: string, accountId: string): string | null {
  const key = accountStorageKey(baseKey, accountId);
  if (!key) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeAccountItem(baseKey: string, accountId: string, value: string): boolean {
  const id = String(accountId || '').trim();
  const key = accountStorageKey(baseKey, id);
  if (!key || persistenceBlockedAccounts.has(id)) return false;
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function removeAccountItem(baseKey: string, accountId: string): void {
  const key = accountStorageKey(baseKey, accountId);
  if (!key) return;
  try { localStorage.removeItem(key); } catch {}
}

export function announceAccountChange(accountId: string, previousAccountId = ''): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(ACCOUNT_CHANGED_EVENT, {
    detail: { accountId, previousAccountId },
  }));
}
